import React, { useEffect, useState } from 'react';
import { Swords } from 'lucide-react';
import LiveChessGame from '../../../common/LiveChessGame';
import { getPlayerProfile } from '../../../../services/firestoreService';
import useBlockStyles from '../common/useBlockStyles';

const STYLE_OPTIONS = {
  defaultBackground: 'section',
  defaultHeadingSize: 'medium',
  defaultAlignment: 'center',
  defaultPadding: 'normal',
};

/**
 * ChessGameRenderer - Renders the interactive chess game block
 * Always shows the game (not hidden based on turn like player profile version)
 */
const ChessGameRenderer = ({ block, profileId, accentColor, theme = {} }) => {
  const content = block.content || {};
  const [username, setUsername] = useState(null);
  const [profileDisplayName, setProfileDisplayName] = useState(null);
  const [profileOwnerId, setProfileOwnerId] = useState(null);
  const [loading, setLoading] = useState(true);

  const styles = useBlockStyles(content, theme, STYLE_OPTIONS);

  // Load profile data needed for chess game
  useEffect(() => {
    const loadProfileData = async () => {
      if (!profileId) {
        setLoading(false);
        return;
      }

      try {
        // Get profile data which includes username and owner ID
        const profileData = await getPlayerProfile(profileId);
        if (profileData) {
          setUsername(profileData.username || '');
          setProfileDisplayName(profileData.displayName || '');
          setProfileOwnerId(profileData.ownerId || profileData.userId || null);
        }
      } catch (error) {
        console.error('Error loading profile data for chess game:', error);
      } finally {
        setLoading(false);
      }
    };

    loadProfileData();
  }, [profileId]);

  // Build chess game config from block content
  const chessGameConfig = {
    enabled: true,
    ownerColor: content.ownerColor || 'white',
    matchThemeSquares: content.matchThemeSquares === true,
  };

  // Placeholder when no profile
  if (!profileId) {
    return (
      <div className="chess-game-block-placeholder">
        <div className="placeholder-icon">
          <Swords size={48} />
        </div>
        <h3>Chess Game</h3>
        <p>Connect a profile to enable the interactive chess game.</p>
        <style>{`
          .chess-game-block-placeholder {
            padding: 60px 24px;
            text-align: center;
            background: ${styles.sectionBackground};
            border-radius: 16px;
            border: 2px dashed ${styles.accentColor}4d;
          }
          .placeholder-icon {
            width: 80px;
            height: 80px;
            margin: 0 auto 20px;
            background: ${styles.accentColor}1a;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            color: ${styles.accentColor};
          }
          .chess-game-block-placeholder h3 {
            font-size: 1.25rem;
            font-weight: 600;
            color: ${styles.headingColor};
            margin-bottom: 8px;
          }
          .chess-game-block-placeholder p {
            color: ${styles.mutedColor};
            font-size: 0.95rem;
          }
        `}</style>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="chess-game-block-loading">
        <div className="loading-spinner"></div>
        <span>Loading chess game...</span>
        <style>{`
          .chess-game-block-loading {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            padding: 80px 24px;
            color: ${styles.mutedColor};
          }
          .loading-spinner {
            width: 40px;
            height: 40px;
            border: 3px solid ${styles.accentColor}33;
            border-top-color: ${styles.accentColor};
            border-radius: 50%;
            animation: chess-spin 1s linear infinite;
            margin-bottom: 16px;
          }
          @keyframes chess-spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div className="chess-game-block-outer">
      <div className="chess-game-block">
        <LiveChessGame
          profileId={profileId}
          profileOwnerId={profileOwnerId}
          chessGameConfig={chessGameConfig}
          isOwnerView={false}
          accentColor={styles.accentColor}
          profileUsername={username || ''}
          profileDisplayName={profileDisplayName || ''}
          showTurnBanner={false}
          isTransitioning={false}
          title={content.title || 'Play Chess With Me'}
          headingColor={styles.headingColor}
          bodyColor={styles.bodyColor}
          mutedColor={styles.mutedColor}
          isDark={styles.isDark}
        />
      </div>

      <style>{`
        .chess-game-block-outer {
          background: ${styles.sectionBackground};
        }

        .chess-game-block {
          padding: 40px 24px;
          max-width: 1200px;
          margin: 0 auto;
        }

        @media (max-width: 768px) {
          .chess-game-block {
            padding: 32px 16px;
          }
        }
      `}</style>
    </div>
  );
};

export default ChessGameRenderer;
